import { supabase, User, AuthResponse } from '../lib/supabase'
import bcrypt from 'bcryptjs'

// Re-export User interface for other components
export type { User } from '../lib/supabase'

export interface LoginCredentials {
  email: string;
  password: string;
  role?: 'coach' | 'headteacher' | 'headmaster' | 'admin';
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  schoolId?: string;
  role: 'coach' | 'headteacher' | 'headmaster' | 'admin';
}

class AuthService {
  private currentUser: User | null = null;

  // Helper function to transform database user to User interface
  private transformDatabaseUser(dbUser: any): User {
    return {
      id: dbUser.id,
      email: dbUser.email,
      firstName: dbUser.first_name,
      lastName: dbUser.last_name,
      phoneNumber: dbUser.phone_number,
      schoolId: dbUser.school_id,
      role: dbUser.role,
      isActive: dbUser.is_active,
      created_at: dbUser.created_at,
      updated_at: dbUser.updated_at
    }
  }

  // Register a new user (coach)
  async register(userData: RegisterData): Promise<AuthResponse> {
    try {
      // Hash the password
      const hashedPassword = await bcrypt.hash(userData.password, 10)
      
      // Check if user already exists
      const { data: existingUser } = await supabase
        .from('sports_registration_app_users')
        .select('email')
        .eq('email', userData.email)
        .single()
      
      if (existingUser) {
        return {
          success: false,
          message: 'User with this email already exists',
          error: 'EMAIL_EXISTS'
        }
      }
      
      // Insert new user
      const { data, error } = await supabase
        .from('sports_registration_app_users')
        .insert([
          {
            email: userData.email,
            password_hash: hashedPassword,
            first_name: userData.firstName,
            last_name: userData.lastName,
            school_id: userData.schoolId,
            role: userData.role || 'coach',
            is_active: true
          }
        ])
        .select()
        .single()
      
      if (error) {
        console.error('Registration error details:', {
          error: error,
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        return {
          success: false,
          message: `Failed to create user account: ${error.message}`,
          error: error.message
        }
      }
      
      // Transform and return user without password
      const user = this.transformDatabaseUser(data)
      
      return {
        success: true,
        message: 'User registered successfully',
        user: user
      }
      
    } catch (error: any) {
      console.error('Registration error:', error)
      return {
        success: false,
        message: 'Registration failed',
        error: error.message
      }
    }
  }

  // Extract school code from headmaster email
  private extractSchoolCodeFromEmail(email: string): string | null {
    // Headmaster email format: hm.schoolcode@moe.edu.gy (school code in lowercase in email)
    const emailPattern = /^hm\.([a-zA-Z0-9]+)@moe\.edu\.gy$/i;
    const match = email.match(emailPattern);
    return match ? match[1].toLowerCase() : null; // Extract as lowercase
  }

  // Find school by code
  async findSchoolByCode(schoolCode: string): Promise<{ id: string; name: string } | null> {
    try {
      // Convert lowercase school code to uppercase for database lookup
      const upperSchoolCode = schoolCode.toUpperCase();
      
      const { data: school, error } = await supabase
        .from('sms_schools')
        .select('id, name')
        .eq('code', upperSchoolCode)  // Exact match with uppercase
        .single()
      
      if (error || !school) {
        return null
      }
      
      return school
    } catch (error) {
      console.error('Find school error:', error)
      return null
    }
  }

  // Register headmaster with email-based school detection
  async registerTeacher(teacherData: { 
    email: string; 
    password: string; 
    firstName: string; 
    lastName: string; 
  }): Promise<AuthResponse> {
    try {
      // Extract school code from email
      const schoolCode = this.extractSchoolCodeFromEmail(teacherData.email);
      if (!schoolCode) {
        return {
          success: false,
          message: 'Invalid headmaster email format. Expected format: hm.schoolcode@moe.edu.gy',
          error: 'INVALID_EMAIL_FORMAT'
        }
      }

      // Find school by code
      const school = await this.findSchoolByCode(schoolCode);
      if (!school) {
        return {
          success: false,
          message: `School with code '${schoolCode}' not found`,
          error: 'SCHOOL_NOT_FOUND'
        }
      }

      // Register the teacher
      const registrationData: RegisterData = {
        email: teacherData.email,
        password: teacherData.password,
        firstName: teacherData.firstName,
        lastName: teacherData.lastName,
        schoolId: school.id,
        role: 'headmaster'  // Changed from 'headteacher' to match database constraint
      };

      return await this.register(registrationData);
    } catch (error: any) {
      console.error('Teacher registration error:', error)
      return {
        success: false,
        message: 'Teacher registration failed',
        error: error.message
      }
    }
  }

  // Enhanced login that handles teacher auto-registration
  async loginWithTeacherRegistration(credentials: LoginCredentials & { 
    firstName?: string; 
    lastName?: string; 
  }): Promise<{ user: User; token: string; needsRegistration?: boolean }> {
    try {
      // Check if this is a headmaster email format
      const schoolCode = this.extractSchoolCodeFromEmail(credentials.email);
      const isHeadmasterEmail = schoolCode !== null;
      
      // If it's a headmaster email but wrong role, suggest the correct role
      if (isHeadmasterEmail && credentials.role !== 'headmaster' && credentials.role !== 'headteacher') {
        throw new Error('Headmaster emails must use the "Headteacher" role');
      }
      
      // First try normal login
      try {
        return await this.login(credentials);
      } catch (loginError) {
        // If login fails and this is a headmaster email, check if we should register
        if (isHeadmasterEmail && (credentials.role === 'headmaster' || credentials.role === 'headteacher')) {
          // Check if user doesn't exist (not just wrong password)
          const { data: existingUser } = await supabase
            .from('sports_registration_app_users')
            .select('id')
            .eq('email', credentials.email)
            .single()
          
          if (!existingUser) {
            // User doesn't exist, need registration data
            if (!credentials.firstName || !credentials.lastName) {
              throw new Error('NEEDS_REGISTRATION');
            }
            
            // Auto-register the headmaster
            const registrationResult = await this.registerTeacher({
              email: credentials.email,
              password: credentials.password,
              firstName: credentials.firstName,
              lastName: credentials.lastName
            });
            
            if (!registrationResult.success) {
              throw new Error(registrationResult.message);
            }
            
            // Now try to login again
            return await this.login(credentials);
          }
        }
        
        // Re-throw the original error if not a teacher registration case
        throw loginError;
      }
    } catch (error: any) {
      throw new Error(error.message || 'Login failed')
    }
  }

  // Login user
  async login(credentials: LoginCredentials): Promise<{ user: User; token: string }> {
    try {
      // Get user from database
      const { data: user, error } = await supabase
        .from('sports_registration_app_users')
        .select('*')
        .eq('email', credentials.email)
        .single()
      
      if (error || !user) {
        throw new Error('Invalid email or password')
      }
      
      // Check password
      const passwordMatch = await bcrypt.compare(credentials.password, user.password_hash)
      
      if (!passwordMatch) {
        throw new Error('Invalid email or password')
      }
      
      // Check role if specified
      if (credentials.role && user.role !== credentials.role) {
        throw new Error('Invalid role for this user')
      }
      
      // Transform user data
      const transformedUser = this.transformDatabaseUser(user)
      
      this.currentUser = transformedUser
      
      // Store in localStorage for persistence
      const token = 'supabase_token_' + user.id
      localStorage.setItem('auth_token', token)
      localStorage.setItem('current_user', JSON.stringify(transformedUser))
      
      return { 
        user: transformedUser, 
        token 
      }
      
    } catch (error: any) {
      throw new Error(error.message || 'Login failed')
    }
  }

  // Check if user is currently logged in
  getCurrentUser(): User | null {
    if (this.currentUser) {
      return this.currentUser;
    }

    // Try to restore from localStorage
    const storedUser = localStorage.getItem('current_user');
    const token = localStorage.getItem('auth_token');
    
    if (storedUser && token) {
      this.currentUser = JSON.parse(storedUser);
      return this.currentUser;
    }

    return null;
  }

  // Logout user
  async logout(): Promise<void> {
    this.currentUser = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.getCurrentUser() !== null;
  }

  // Get auth token
  getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  // Get user by ID
  async getUserById(id: string): Promise<User | null> {
    try {
      const { data, error } = await supabase
        .from('sports_registration_app_users')
        .select('*')
        .eq('id', id)
        .single()
      
      if (error || !data) {
        return null
      }
      
      return this.transformDatabaseUser(data)
    } catch (error) {
      console.error('Get user error:', error)
      return null
    }
  }

  // Helper method to create admin user (for initial setup)
  async createAdminUser(adminData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
  }): Promise<AuthResponse> {
    return this.register({
      ...adminData,
      role: 'admin'
    });
  }

  // Check if email exists in the system (for password reset)
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('sports_registration_app_users')
        .select('id')
        .eq('email', email)
        .single();
      
      return !error && !!data;
    } catch (error) {
      console.error('Check email error:', error);
      return false;
    }
  }

  // Reset password with verification
  async resetPassword(email: string, newPassword: string): Promise<AuthResponse> {
    try {
      // Hash the new password
      const hashedPassword = await bcrypt.hash(newPassword, 10);
      
      // Update password in database
      const { error } = await supabase
        .from('sports_registration_app_users')
        .update({
          password_hash: hashedPassword,
          updated_at: new Date().toISOString()
        })
        .eq('email', email)
        .select()
        .single();
      
      if (error) {
        console.error('Password reset error:', error);
        return {
          success: false,
          message: 'Failed to reset password. Please try again.',
          error: error.message
        };
      }
      
      return {
        success: true,
        message: 'Password reset successfully. You can now login with your new password.'
      };
      
    } catch (error: any) {
      console.error('Password reset error:', error);
      return {
        success: false,
        message: 'Failed to reset password. Please try again.',
        error: error.message
      };
    }
  }
}

export const authService = new AuthService();
